import { Localization } from "OW.Remoting/Remoting/Localization";
import { OWTime } from "../../Global/Base/DataTypes/OWTime";
import { EnumUtils } from "../../Global/Base/Utils/EnumUtils";
import { Globalization, ITimeInfo } from "../../Global/Dom/Globalization";
import { createElement, registerCustomElement } from "../UI.ElementarControls/Utils/CustomElementUtils";
import { EditableControl } from "./EditableControl";

export class TimeEdit extends EditableControl {
    public twoDigitHour: boolean = true;

    protected override _innerElement: HTMLInputElement;

    private _originalValue: OWTime = null;
    private _timeFormat: TimeFormat = TimeFormat.tfSec;
    private _localeTimeFormat: ITimeInfo;
    private _list: { [text: string]: (e: Event) => void };
    private _testPattern: RegExp;
    private _validTimePattern: RegExp;
    private _edited: boolean = false;
    private _placeholderLocKey: string;

    public get timeFormat(): TimeFormat {
        return this._timeFormat;
    }

    public override set value(val: any) {
        let finalValue: any = val;
        if (val instanceof Date) {
            finalValue = val;
        } else if (typeof val === "string") {
            finalValue = this._parseTime(val);
        }

        this.controlValue = finalValue;
    }

    public override get value(): any {
        return this._originalValue; //this.formatVal(this.originalValue);
    }

    public set controlValue(value: Date) { // prijima Date
        if (value) { // neprazdna hodnota
            if ((value instanceof Date) && !isNaN(value.getTime())) { // spravne
                this._originalValue = new OWTime(value);
                this._innerElement.value = this._formatVal(value);
            } else {
                this._originalValue = null;
                this._innerElement.value = "";
            }
        } else { // prazdna hodnota
            this._originalValue = null;
            this._innerElement.value = "";
        }
    }

    public get controlValue(): OWTime {
        return this._originalValue;
    }

    public get placeholderLocKey(): string {
        return this._placeholderLocKey;
    }
    public set placeholderLocKey(value: string) {
        this._placeholderLocKey = value;
        if (this._innerElement) {
            if (value) {
                Localization.addLocTxtData(this._innerElement, this.placeholderLocKey, "placeholder");
                return;
            }
            Localization.remLocData(this._innerElement);
        }
    }

    protected static override get observedAttributes(): string[] {
        return ["timeformat", "culture"].concat(EditableControl.observedAttributes);
    }

    protected override _constructor(): void {
        super._constructor();
        //this.shadowRoot = (this as any).createShadowRoot();
        const tcont: string = this.textContent.trim();
        this.innerHTML = "";
        this._list = {};

        this._innerElement = createElement({ type: "input" }) as HTMLInputElement;
        this._innerElement.setAttribute("autocomplete", "off");

        this.appendChild(this._innerElement);

        this._localeTimeFormat = Globalization.timeFormat;
        this.twoDigitHour = true;
        this.classList.add("TimeEdit");

        if (tcont.length > 0) {
            this.value = tcont;
        }

        this._setAttrs();
        this._setPattern();
    }

    // You can also define the other lifecycle methods.
    protected override connectedCallback(): void {
        super.connectedCallback();

        this._setListeners();
    }

    protected override attributeChangedCallback(attrName: string, oldVal: string, newVal: string): void {
        super.attributeChangedCallback(attrName, oldVal, newVal);

        switch (attrName) {
            case "timeformat": {
                const tFormat = EnumUtils.getEnumValueFromName(XGenTimeFormat, newVal, true) || newVal;
                this._timeFormat = EnumUtils.getEnumValueFromName(TimeFormat, tFormat) || TimeFormat.tfMin;
                this._formatRequest();
                break;
            }
            case "culture": {
                this._culture = newVal;
                this._culture && this._culture !== Globalization.actualCulture ? Globalization.getTimeFormat(this._culture) : Globalization.timeFormat;
                this._formatRequest();
                break;
            }
        }
    }

    /**
     * Metoda na zpracování události při změně jazyka a culture
     */
    private _formatRequest(): void {
        this._setPattern();
        if (this._originalValue != null && !isNaN(this._originalValue.getTime()))
            this._innerElement.value = this._formatVal(this._originalValue);
    }

    /**
     * Základní nastavení formátovaci regularnich výrazů
     */
    private _setPattern(): void {
        // Nastavení cultury podle elementu
        this._localeTimeFormat = this._culture && this._culture !== Globalization.actualCulture ? Globalization.getTimeFormat(this._culture) : Globalization.timeFormat;

        this._validTimePattern = this._localeTimeFormat.regExp.timeTypeValid;
        switch (this._timeFormat) {
            case TimeFormat.tfHour:
                this._testPattern = this._localeTimeFormat.regExp.timeHour;
                break;
            case TimeFormat.tfMin:
                this._testPattern = this._localeTimeFormat.regExp.timeMin;
                break;
            case TimeFormat.tfSec:
                this._testPattern = this._localeTimeFormat.regExp.timeSec;
                break;
        }
    }

    /**
     * Nastavení nativních události
     */
    private _setListeners(): void {
        const f_focusTimeout = () => {
            const lCurosPos = EditableControl.getInputSelection(this._innerElement).start;

            if (this._originalValue) {
                EditableControl.setCaret(this._innerElement, lCurosPos);
            }
        };

        this._innerElement.addEventListener("focus", this._list["focus"] = () => {
            // timeout, protoze dochazi k prechodu do editace pri kliku do pole, pokud bude pole po timeoutu v editaci dojde ke zpracovani
            self.setTimeout(() => {
                if (!this._innerElement.readOnly)
                    f_focusTimeout();
            }, 150);
        });

        this._innerElement.addEventListener("keyup", this._list["keyup"] = (e: KeyboardEvent) => {
            if (this._innerElement.readOnly)
                return;

            if (e.keyCode == 13) {
                this.validate();
                return;
            }

            const val = this._innerElement.value;

            if (this._checkTypingValue(val)) {
                this._edited = true;
            }
        });
    }

    public override validate(): boolean {
        const val = this._innerElement.value;
        if (val === "") {
            this.setValid();
            this.controlValue = null;
            return this.isValid;
        }

        if (!this._checkBlurValue()) {
            this.setInvalid("VAL-0");
        } else {
            this.setValid();
        }

        if (this._edited) {
            this._edited = false;
        }

        return this.isValid;
    }

    /**
     * Kontrola hodnoty inputu prováděná při psani
     */
    private _checkTypingValue(val: string): boolean {
        if (val === "")
            return true;
        return this._validTimePattern.test(val);
    }

    /**
     * Metoda volana po zmene inputu, na doplneni hodnot
     */
    private _normalizeValue(): boolean {
        const val = this._innerElement.value;
        if (!this._checkTypingValue(val)) {
            this._originalValue = null;
            return false;
        }

        const newTime = this._parseTime(val);

        this._originalValue = new OWTime(newTime);
        this._innerElement.value = this._formatVal(newTime);

        return true;
    }

    protected override _afterChange(): void {
        this._normalizeValue();
        if (this.validate()) {
            this.callEvent("change", undefined, false, true);
        }
    }

    /**
     * Parsuje hodnotu ze vstupu
     * @example Příklad validního uživatelského vstupu je 1 => 1:00:00, 1001 => 10:01:00, 1:1 => 1:01:00, ...
     * @param val uživatelský vstup
     */
    private _parseTime(val: string): Date {
        const time: string[] = ["", "", ""];
        const min_num = this._localeTimeFormat.hour12 ? 1 : 2;
        const max_next_num = this._localeTimeFormat.hour12 ? 2 : 4;
        let fNum = 0;
        // temp funkce na zpracovani stringu jako čisla
        const f_getNum = (n: string): number => {
            const num = parseInt(n);
            if (isNaN(num))
                return 0;
            else
                return num;
        };

        for (let i = 0, j = 0, z = 0, lng = val.length; i < lng; i++) {
            if (val[i] === this._localeTimeFormat.separator) {
                z++;
                j = 0;
                continue;
            }
            if (j === 2) {
                j = 0;
                z++;
            }
            if (z > 2)
                break;

            if (/\d/.test(val[i])) {
                // pokud obsahuje na prvnim miste cislo vetsi nez maximalni doplni na zacatek nulu, jinak to k datu na zacatku pricitalo dny
                const pNum = parseInt(val[i]);
                if (z === 0 && j === 0) {
                    fNum = pNum;
                    if (pNum > min_num) {
                        time[z] += "0";
                        j++;
                    }
                }
                // pokud predchozi cislo obsazhuje maximalni cislo zadane do dne a dalsi cislo obsahuje vetsi nez koncove cislo dne, je pridana na zacatek 0
                // priklad: pokud bych zadal (ve 24 hod formatu) 25, predchozi podminkou by to proslo, ale datum se presto o jeden den zvedl
                if (z === 0 && j === 1) {
                    if (fNum === min_num && pNum > max_next_num) {
                        time[z] = `0${time[z]}`;
                        j = 0;
                        z++;
                    }
                }
                time[z] += val[i];
                j++;
            }
        }

        // kontrola na zadani hodnoty PM
        let hour = f_getNum(time[0]);
        let rtext: string;
        if (this._localeTimeFormat.amPmOnRight)
            rtext = `.+\\s?${this._localeTimeFormat.pmText}$`;
        else
            rtext = `${this._localeTimeFormat.pmText}\\s?.+$`;
        const reg = new RegExp(rtext, "i");

        // prevadeni na spravne hodnoty PM
        if (this._localeTimeFormat.useAmPm) {
            ///.+\s?PM$/i.test(val)) {
            if (reg.test(val)) {
                if (hour !== 12)
                    hour += 12;
            } else if (hour === 12) { // pokud je 12 hodinovy format, cas je v AM a je roven 12, je to upraveno na 0, dochazelo k prepisu na AM a PM
                hour = 0;
            }
        }

        return new Date(2012, 11, 21, hour, f_getNum(time[1]), f_getNum(time[2]));
    }

    /**
     * Vráti naformátovaný čas podle aktuální kultůry
     * @param val Čas podle kterého se bude formátovat
     */
    private _formatVal(val: Date): string {
        //let printDate = new Date(val.getTime());
        // TODO: (21. 3. 2017) Kvůli chybě v nativní funkci toLocaleTimeString se při zadání data menšího než "25.10.1891" a následného zadaní času snižuje čas. např: 1.1.1890 12:00:00 - čas se zmenší na 11:57:44
        const printDate = new Date(2000, 1, 1, val.getHours(), val.getMinutes(), val.getSeconds(), val.getMilliseconds());
        const opt: any = { hour: "2-digit", minute: "2-digit" };

        if (!this.twoDigitHour)
            opt.hour = "numeric";

        if (this._timeFormat === TimeFormat.tfHour) {
            //printDate.setMinutes(0);
            opt.minute = undefined;
        } else if (this._timeFormat === TimeFormat.tfSec) {
            opt.second = "2-digit";
        }

        //return printDate.toLocaleTimeString(this.Culture ? this.Culture : Globalization.ActualCulture, opt);
        let finalDate = printDate.toLocaleTimeString(this._culture ? this._culture : Globalization.actualCulture, opt);

        const t_hour = printDate.toLocaleTimeString(this._culture ? this._culture : Globalization.actualCulture, { hour: "2-digit" });
        // TODO: (10. 2. 2016) v opt hour: 2-digit funguje spravne jen v Edge, v ostatnich prohlížečích se zobrazí jedno číslo, byla k tomu třeba udělat berlička, až to bude fungovat odstranit
        if (this.twoDigitHour && t_hour.length < 2)
            finalDate = `0${finalDate}`;

        return finalDate;
    }

    /**
     * Kontrola prováděna při opuštění inputu
     */
    private _checkBlurValue(): boolean {
        const val = this._innerElement.value;

        return this._testPattern.test(val);
    }

    private _setAttrs(): void {
        this._innerElement.type = "text"; // default; type="text" kvuli zadavani ':'
        this._innerElement.maxLength = 15;
        this._timeFormat = TimeFormat.tfSec; // tfsec je defaultni
    }

    public override dispose(): void {
        super.dispose();
        this._innerElement.removeEventListener("change", this._list["change"]);
        this._innerElement.removeEventListener("focus", this._list["focus"]);
        this._innerElement.removeEventListener("keyup", this._list["keyup"]);
    }
}

export enum TimeFormat {
    tfHour = 1,
    tfMin = 2,
    tfSec = 3,
}

/* eslint-disable @typescript-eslint/no-duplicate-enum-values */
export enum XGenTimeFormat {
    T = "tfSec",
    t = "tfMin",
    F = "tfSec",
    f = "tfMin",
    G = "tfSec",
    g = "tfMin",
    O = "tfSec",
    o = "tfSec",
    r = "tfSec",
    R = "tfSec",
    s = "tfSec",
    u = "tfSec",
    U = "tfSec",
}

registerCustomElement("tsc-timeedit", TimeEdit);
